import React, { JSX } from 'react';
import { ReactComponent as PhoneCircleSvg } from '../icons/phone_circle.svg';
import GenericCard, { GenericContentType } from './GenericCard';
import GenericCarousel from './GenericCarousel';
import { ANALYTICS_EVENT_TYPES, logEvent } from '../analytics';
import generalConfig from '../resources/general.json';
import { AmpFlagSet } from '../amplitude';
import { getUrlParam } from '../helpers';
import { postAnchorClickEventToParentApp } from '../messageassist';

export interface MedicationListv2Pharmacy {
    name: string;
    phone: string;
    address: string;
    city: string;
    state: string;
    zipcode: string;
}

export interface MedicationListv2Prescription {
    name: string;
    provider: string;
    refills: number;
    status: string;
    pharmacy: string;
}

export interface MedicationListv2PrescriptionData {
    providers: { [key: string]: string };
    pharmacies: { [key: string]: MedicationListv2Pharmacy };
    user_prescriptions: MedicationListv2Prescription[];
}

export interface MedicationListAttachmentDatav2 {
    prescriptionData: MedicationListv2PrescriptionData;
    type: string;
    removeBubble: boolean;
    hideAvatar: boolean;
    noMargin: boolean;
    hidePhoneNumbers: boolean;
    hideInstructions: boolean;
}

function getPhoneNumbersOnlyv2(unformattedPhone: string): string {
    return unformattedPhone.replace(/[^0-9]/g, '');
}

function toPhoneDisplayv2(phoneNumbers: string): string {
    return `Call pharmacy (${phoneNumbers.substr(0, 3)}) ${phoneNumbers.substr(
        3,
        3
    )}-${phoneNumbers.substr(6, 4)}`;
}

function logPhoneHrefClickv2() {
    logEvent(ANALYTICS_EVENT_TYPES.MED_CALL_PHARMACY, {
        click_call_type: 'number',
    });
}

export const MedicationListv2: React.FC<{
    featureFlags: AmpFlagSet;
    prescriptionData: MedicationListv2PrescriptionData;
    hidePhoneNumbers: boolean;
    hideInstructions: boolean;
    sendMessage: (message: string) => void;
}> = ({
    featureFlags,
    prescriptionData,
    hideInstructions,
    hidePhoneNumbers,
    sendMessage,
}) => {
    const onPhoneHrefClick = async (
        e: any,
        phoneDisplay: string,
        phoneNumber: string
    ) => {
        e.stopPropagation();
        logPhoneHrefClickv2();
        // if there's a host app, let them handle the event instead of the browser.
        if (getUrlParam('hostappchannel')) {
            await postAnchorClickEventToParentApp(
                e,
                `tel:${phoneNumber}`,
                phoneDisplay
            );
        }
    };

    const onItemClick = async (e: any, text: string) => {
        sendMessage(text);
    };
    const pharmacyElements: JSX.Element[] = [];

    const medicationElts = prescriptionData.user_prescriptions.map(
        (medication: MedicationListv2Prescription) => {
            const pharmacyPhone =
                prescriptionData.pharmacies[medication.pharmacy].phone;
            const phoneNumbers = pharmacyPhone
                ? getPhoneNumbersOnlyv2(pharmacyPhone)
                : '';
            const phoneDisplay =
                pharmacyPhone && phoneNumbers.length === 10
                    ? toPhoneDisplayv2(phoneNumbers)
                    : '';

            const medName = medication.name;

            let secondTitleLine;

            if (medication.status.toLowerCase() === 'expired') {
                secondTitleLine = { text: 'Expired' };
            }

            const medicationList = {
                type: GenericContentType.Quantity,
                title: { text: medName },
                ...(secondTitleLine && { secondTitleLine: secondTitleLine }),
                details: {
                    text: hidePhoneNumbers ? '' : phoneDisplay,
                    isPhoneNumber: !hidePhoneNumbers,
                    newTab: true,
                    phoneNumberNumerical: pharmacyPhone,
                    onClick: async (e: any) =>
                        await onPhoneHrefClick(e, phoneDisplay, pharmacyPhone),
                    image: <PhoneCircleSvg />,
                    instruction: generalConfig.providence.medicationListSelect,
                },
                quantity: medication.refills,
                quantityEmphasizeZero: true,
                label: generalConfig.providence.medicationListRefills,
                onClick: async (e: any) => await onItemClick(e, medName),
            };

            if (hideInstructions) {
                if (medicationList.details.instruction) {
                    delete (medicationList.details as { instruction?: string })
                        .instruction;
                }

                delete (medicationList as { onClick?: (e: any) => void })
                    .onClick;
            }

            return medicationList;
        }
    );

    pharmacyElements.push(
        <GenericCard
            data={{
                title: { text: 'Your Medications' },
                content: medicationElts,
            }}
            featureFlags={featureFlags}
        />
    );

    return <GenericCarousel elements={pharmacyElements} />;
};

export default MedicationListv2;
